import { useEffect, useState } from "react"
import posthog from "posthog-js"
import RootLayout from "./Root"
import Logo from "@/components/Logo"
import { router, usePage, Link } from "@inertiajs/react"
import timeZones from "@/utils/timeZones"
import clsx from "clsx"
import { User } from "@/types/types"

const EmailForm = ({ email }: { email: string }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const saveChanges = async () => {
    setIsSubmitting(true)
    router.put(
      "/user",
      { email },
      {
        onFinish: () => {
          setIsSubmitting(false)
          setIsEditing(false)
        },
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  return isEditing ? (
    <div className="relative">
      <input
        type="email"
        required
        autoFocus
        disabled={isSubmitting}
        className="w-full border-none bg-transparent text-center text-teal-300 outline-none"
        defaultValue={email}
      />
      <button
        className="absolute top-full left-1/2 mt-1 h-6 -translate-x-1/2 transform justify-center rounded bg-gray-700 px-2 text-xs"
        onClick={saveChanges}
        disabled={isSubmitting}
      >
        <span className={clsx({ "opacity-0": isSubmitting })}>Done</span>
        {isSubmitting && (
          <div className="absolute top-1/2 left-1/2 -mt-2 -ml-2 h-3 w-3 animate-spin rounded-full border-2 border-white border-r-transparent" />
        )}
      </button>
    </div>
  ) : (
    <button className="text-teal-300" onClick={() => setIsEditing(true)}>
      {email}
    </button>
  )
}

const TimeZoneForm = ({ initialTimeZone, timeZones }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const saveChanges = async (time_zone) => {
    setIsSubmitting(true)
    router.put(
      "/user",
      { time_zone },
      {
        onFinish: () => setIsSubmitting(false),
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  return (
    <div className="relative">
      <div className="relative z-0 inline-flex items-center text-teal-300">
        {initialTimeZone || "Please choose…"}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="-mr-2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <select
        tabIndex={-1}
        defaultValue={initialTimeZone}
        disabled={isSubmitting}
        onChange={(e) => saveChanges(e.target.value)}
        className="absolute top-0 bottom-0 left-1/2 z-10 -translate-x-1/2 transform appearance-none text-black opacity-0"
      >
        {timeZones.map((tz, i) => (
          <option key={i}>{tz}</option>
        ))}
      </select>
    </div>
  )
}

const AppLayout = ({ children }) => {
  const { current } = usePage<{
    current: {
      user: User
    }
  }>().props
  const { id, email, time_zone, send_daily_emails, suspended_at, suspended_for_complaint } = current.user

  useEffect(() => {
    document.body.classList.add("bg-gray-900")
    if (id) posthog.identify(String(id), { time_zone, send_daily_emails })
  }, [])

  const toggleDailySummary = async (send_daily_emails: boolean) => {
    router.put(
      "/user",
      { user: { send_daily_emails } },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  return (
    <RootLayout className="bg-gray-900 text-white">
      <header className="mt-10 flex items-center justify-center">
        <Link href="/">
          <Logo />
        </Link>
      </header>

      <main className="flex-1">
        {suspended_at && (
          <div className="mx-auto mt-6 w-full max-w-2xl rounded-lg border border-amber-700 bg-amber-900/50 p-4 text-center text-sm text-amber-200">
            {suspended_for_complaint ? (
              <>
                <p className="mb-2">
                  Your account has been suspended because your email provider
                  flagged our messages as spam.
                </p>
                <p className="text-xs text-amber-300/70">
                  Please contact support to restore your account.
                </p>
              </>
            ) : (
              <>
                <p className="mb-2">
                  Your account has been suspended because emails to your address
                  are bouncing.
                </p>
                <p className="mb-3 text-xs text-amber-300/70">
                  If you've fixed the issue, reactivate your account to resume
                  alerts.
                </p>
                <button
                  onClick={() =>
                    router.post(
                      "/user/reactivate",
                      {},
                      { preserveState: false }
                    )
                  }
                  className="rounded bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500"
                >
                  Reactivate my account
                </button>
              </>
            )}
          </div>
        )}
        {children}
      </main>

      <footer className="bg-gray-800 p-10 text-center text-sm">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col lg:flex-row">
            <div className="mb-6 flex-1 leading-relaxed lg:mb-0">
              <div>Send alerts to:</div>
              <EmailForm email={email} />
            </div>
            <div className="mb-6 flex-1 leading-relaxed lg:mb-0">
              <div>Send me a 7am morning update email:</div>
              <button
                className={
                  !send_daily_emails
                    ? "text-gray-600 underline hover:text-white hover:no-underline"
                    : "text-teal-300"
                }
                onClick={() => toggleDailySummary(true)}
              >
                Yes, please
              </button>
              <span className="text-gray-600"> / </span>
              <button
                className={
                  send_daily_emails
                    ? "text-gray-600 underline hover:text-white hover:no-underline"
                    : "text-teal-300"
                }
                onClick={() => toggleDailySummary(false)}
              >
                No, thanks
              </button>
            </div>
            <div className="flex-1 leading-relaxed">
              <div>Time zone:</div>
              <TimeZoneForm timeZones={timeZones} initialTimeZone={time_zone} />
            </div>
          </div>

          <div className="my-10 flex items-center leading-none">
            <div className="h-px flex-1 bg-gray-700" />
            <a
              href="/logout"
              className="mx-4 text-gray-600 underline hover:text-white hover:no-underline"
            >
              Log out
            </a>
            <div className="h-px flex-1 bg-gray-700" />
          </div>

          <div className="text-center text-gray-600">
            A project by Lakpa SLU.
            <br />
            <Link
              href="/about"
              className="underline hover:text-white hover:no-underline"
            >
              About
            </Link>
            <span className="mx-2">&bull;</span>
            <Link
              href="/privacy"
              className="underline hover:text-white hover:no-underline"
            >
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </RootLayout>
  )
}
export default AppLayout
